import {isPlatformBrowser} from '@angular/common';
import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {JsonService, LocalStorageService, SessionStorageService} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {v4 as uuidV4} from 'uuid';

interface FormUuidList {
  [key: string]: string;
}

const UUID_STORAGE_KEY: string = 'formUuidList';
const SUPPORT_FORM_UUID_STORAGE_KEY: string = 'supportFormId';
const formUuidMaxLength: number = 20;

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class FormUuidService {

  private formUuidList: FormUuidList = {};

  constructor(
    @Inject(PLATFORM_ID) public platformId: object,
    private localStorageService: LocalStorageService,
    private sessionStorageService: SessionStorageService,
    private jsonService: JsonService,
    private router: Router,
  ) {
    this.loadSessionUuidList();
    this.setSupportFormUuid();
    this.router.events.pipe(untilDestroyed(this)).subscribe(e => {
      if (e instanceof NavigationEnd) {
        this.clearSessionUuidList();
      }
    });
  }

  /**
   * Returns a uuid for a given form name.
   *
   * @param name of the form
   * @param refresh force the creation of a new id for the given form name
   */
  public getUuidByFormName(name: string, refresh: boolean = false) {
    if (refresh || !this.formUuidList.hasOwnProperty(name)) {
      this.formUuidList[name] = uuidV4();
      this.saveSessionUuidList();
    }
    return this.formUuidList[name];
  }

  /**
   * Adobe analytics has a 20byte restriction on unique ids so this method returns the same
   * id as {@link #getUuidByFormName} but shortens it to meet these requirements.
   *
   * @param name of the form
   * @param limit the byte or character limit of the trimmed uuid, default: {@code 20}
   * @param refresh force the creation of a new id for the given form name, default: {@code false}
   */
  public getTrimmedUuidByFormName(name: string, limit: number = formUuidMaxLength, refresh: boolean = false) {
    return this.getUuidByFormName(name, refresh).replace(/-/g, '').slice(0, limit);
  }

  public getSupportFormUuid() {
    return this.sessionStorageService.getItem(SUPPORT_FORM_UUID_STORAGE_KEY);
  }

  /**
   * Saves the generated formatted support form uuid in the session storage.
   *
   * @param limit the byte or character limit of the trimmed uuid
   */

  public setSupportFormUuid(limit: number = formUuidMaxLength) {
    const supportFormIdExists = this.getSupportFormUuid();
    if (!supportFormIdExists) {
      const supportFormId = uuidV4().replace(/-/g, '').slice(0, limit);
      this.sessionStorageService.setItem(SUPPORT_FORM_UUID_STORAGE_KEY, supportFormId);
    }
  }

  protected clearSessionUuidList(): void {
    if (isPlatformBrowser(this.platformId) && Object.keys(this.formUuidList).length) {
      this.formUuidList = {};
      this.saveSessionUuidList();
    }
  }

  /**
   * Loads the previously set form uuids from the local storage.
   */
  protected loadSessionUuidList(): void {
    if (isPlatformBrowser(this.platformId) && window.localStorage && document.referrer?.includes(window.location.host)) {
      const sessionFormUuidList: any = this.jsonService.parseSafely(localStorage.getItem(UUID_STORAGE_KEY));
      if (sessionFormUuidList && typeof sessionFormUuidList === 'object') {
        this.formUuidList = sessionFormUuidList as FormUuidList;
      }
    } else {
      this.saveSessionUuidList();
    }
  }

  /**
   * Saves the generated form uuids in the local storage.
   */
  protected saveSessionUuidList(): void {
    this.localStorageService.setItem(UUID_STORAGE_KEY, JSON.stringify(this.formUuidList));
  }
}
